import React from 'react';
import { cn } from '../../lib/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

const variantClasses: Record<NonNullable<SkeletonProps['variant']>, string> = {
  text: 'h-4 rounded-md',
  circular: 'rounded-full',
  rectangular: 'rounded-md',
};

const animationClasses: Record<NonNullable<SkeletonProps['animation']>, string> = {
  pulse: 'animate-pulse',
  wave: 'animate-pulse',
  none: '',
};

export function Skeleton({
  variant = 'text',
  width,
  height,
  animation = 'pulse',
  className,
  ...props
}: SkeletonProps) {
  const style: React.CSSProperties = {
    width: width === undefined ? undefined : typeof width === 'number' ? `${width}px` : width,
    height: height === undefined ? undefined : typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div
      className={cn(
        'flex-shrink-0 bg-neutral-200 dark:bg-neutral-700',
        variantClasses[variant],
        animationClasses[animation],
        className
      )}
      style={style}
      aria-hidden="true"
      {...props}
    />
  );
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  const lineCount = Math.max(0, lines);
  return (
    <div className={cn('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: lineCount }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          width={index === lineCount - 1 ? '60%' : '100%'}
          className={index === lineCount - 1 ? 'ml-auto' : undefined}
        />
      ))}
    </div>
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-4 rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900 sm:p-5', className)}>
      <div className="flex items-center gap-4">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="28%" />
        </div>
      </div>
      <SkeletonText lines={3} />
    </div>
  );
}

export function SkeletonTable({ rows = 5, columns = 4, className }: { rows?: number; columns?: number; className?: string }) {
  const rowCount = Math.max(0, rows);
  const columnCount = Math.max(0, columns);

  return (
    <div className={cn('overflow-x-auto rounded-md border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900', className)}>
      <div role="status" aria-live="polite" className="sr-only">Daten werden geladen...</div>
      <table className="w-full table-fixed text-sm" aria-hidden="true">
        <thead className="border-b border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/60">
          <tr>
            {Array.from({ length: columnCount }).map((_, index) => (
              <th key={index} className="px-3 py-2.5 text-left sm:px-4">
                <Skeleton variant="text" width="80%" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {Array.from({ length: rowCount }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columnCount }).map((_, columnIndex) => (
                <td key={columnIndex} className="px-3 py-2.5 sm:px-4">
                  <Skeleton variant="text" width={columnIndex === 0 ? '90%' : '75%'} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
